import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Plus, Pencil, Trash2, ExternalLink, FileText } from 'lucide-react';
import useTableControls from '../../hooks/useTableControls';
import useAdminPageMeta from '../../hooks/useAdminPageMeta';
import { TableToolbar, TablePagination } from '../../components/admin/TableControls';

export default function PagesListPage() {
  useAdminPageMeta('Pages');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get('/api/admin/pages').then(({ data }) => setPages(data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createPage = async (e) => {
    e.preventDefault();
    const { data } = await api.post('/api/admin/pages', { title, slug, blocks: [], status: 'draft' });
    const id = data.page_id || data.id;
    setShowCreate(false); setTitle(''); setSlug('');
    if (id) nav(`/admin/page-editor?id=${id}`); else load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this page? This cannot be undone.')) return;
    await api.delete(`/api/admin/pages/${id}`);
    load();
  };

  const ctrl = useTableControls(pages, {
    searchFn: (p, q) =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q),
    filters: {
      status: (p, v) => (p.status || 'draft') === v,
    },
    pageSize: 10,
  });

  return (
    <div className="p-4 sm:p-6 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6 md:mb-8">
        <div>
          <h1 className="hero-title text-3xl md:text-4xl text-white">Pages</h1>
          <p className="text-ee-text2">Edit the landing page or build new ones from scratch.</p>
        </div>
        <button data-testid="new-page-btn" onClick={() => setShowCreate(true)} className="self-start sm:self-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-md gold-grad text-black font-semibold text-sm"><Plus size={14} /> New Page</button>
      </div>

      {showCreate && (
        <form onSubmit={createPage} className="ee-card p-5 mb-6 grid md:grid-cols-3 gap-3 items-end">
          <div><label className="text-xs uppercase tracking-wider text-gold/80">Title</label><input required value={title} onChange={(e) => { setTitle(e.target.value); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')); }} className="w-full mt-1 bg-ee-bg border border-ee-border rounded-md px-3 py-2 text-white" placeholder="My new page" /></div>
          <div><label className="text-xs uppercase tracking-wider text-gold/80">Slug</label><input required value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full mt-1 bg-ee-bg border border-ee-border rounded-md px-3 py-2 text-white" placeholder="my-new-page" /></div>
          <div className="flex gap-2"><button type="submit" className="px-4 py-2 rounded-md gold-grad text-black font-semibold text-sm">Create</button><button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-md border border-ee-border text-ee-text2 text-sm">Cancel</button></div>
        </form>
      )}

      <TableToolbar
        query={ctrl.query}
        onQueryChange={ctrl.setQuery}
        placeholder="Search pages by title or slug…"
        filters={[
          {
            key: 'status',
            label: 'Status',
            value: ctrl.filterValues.status,
            onChange: (v) => ctrl.setFilter('status', v),
            options: [
              { value: 'all', label: 'All status' },
              { value: 'published', label: 'Published' },
              { value: 'draft', label: 'Draft' },
            ],
          },
        ]}
        pageSize={ctrl.pageSize}
        onPageSizeChange={ctrl.setPageSize}
      />

      <div className="ee-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-ee-bg2 text-ee-muted">
            <tr className="text-left">
              <th className="p-3">Title</th>
              <th className="p-3">Slug</th>
              <th className="p-3">Blocks</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ee-border">
            {loading && <tr><td colSpan="5" className="p-6 text-center text-ee-muted">Loading…</td></tr>}
            {!loading && ctrl.filteredCount === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-ee-muted">
                {ctrl.total === 0 ? 'No pages yet.' : 'No pages match your filters.'}
              </td></tr>
            )}
            {ctrl.pageItems.map((p) => (
              <tr key={p.page_id} className="hover:bg-ee-card2">
                <td className="p-3"><div className="flex items-center gap-2"><FileText size={14} className="text-gold/70" /><span className="text-white">{p.title}</span></div></td>
                <td className="p-3 text-ee-text2 font-mono text-xs">/{p.slug}</td>
                <td className="p-3 text-ee-text2">{(p.blocks || []).length}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs border ${p.status === 'published' ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}`}>{p.status || 'draft'}</span></td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Link to={p.slug === 'home' ? '/' : `/p/${p.slug}`} target="_blank" className="text-ee-muted hover:text-gold"><ExternalLink size={14} /></Link>
                    <Link to={`/admin/page-editor?id=${p.page_id}`} data-testid={`edit-page-${p.slug}`} className="text-gold hover:text-white inline-flex items-center gap-1 text-xs"><Pencil size={12} /> Edit</Link>
                    {p.slug !== 'home' && <button onClick={() => remove(p.page_id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <TablePagination
          page={ctrl.page}
          pageCount={ctrl.pageCount}
          total={ctrl.total}
          filteredCount={ctrl.filteredCount}
          onPageChange={ctrl.setPage}
        />
      </div>
    </div>
  );
}
